import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../base/Modal";
import Icon from "../base/Icon";
import { getHub, useGlobal, useSubscriptionVersion } from "../../core/Global";
import { UserTypes } from "../../core/Enums";
import { SipPhoneManager } from "../../core/SipPhoneManager";
import "./GrowthOffer.css";


interface OfferLine {
    label?: string
    amount: number
}

interface Offer {
    id: string
    trigger?: string
    style?: "banner" | "modal"
    headline?: string
    text?: string
    alternative?: string
    planCode?: string
    planName?: string
    currentPlanName?: string
    currentMonthly: number
    planMonthly: number
    saving: number
    currentLines: OfferLine[]
    planLines: OfferLine[]
}

const MODAL_ID = "modal-growth-offer"

const money = (v?: number) => "$" + (v ?? 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })


/**
 * The in-product upgrade offer (spec §1.7): shown the moment an account hits a limit, with the
 * plan that fits and the arithmetic behind it. A strip above the page at 80% of an allowance, a
 * small window for everything else.
 *
 * The hub decides whether there is one -- at most one at a time, the same kind at most weekly,
 * nothing a customer put off for a month -- and says so through the account's single
 * "SubscriptionChanged" signal. Only the owner and the account's admins ever see it, and never
 * in the middle of a call: it waits until the call is over.
 */
export default function GrowthOffer() {

    const navigate = useNavigate()
    const [globalState] = useGlobal()
    const live = useSubscriptionVersion()

    const [offer, offerSet] = useState<Offer>()
    const [onCall, onCallSet] = useState<boolean>(() => !!SipPhoneManager.instance.getActiveCall())
    const [math, mathSet] = useState<boolean>(false)

    const user = globalState.user
    const buyer = !!user && !user.isTech && (user.isCustomer || user.userType === UserTypes.Admin)

    // Asked when the page loads and whenever the hub says something on the plan changed.
    useEffect(() => {
        if (!buyer) return
        getHub().Get("/accounts/growth/offer", res => {
            offerSet(res?.success && res.data?.id ? res.data : undefined)
        }, () => { })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyer, live])

    // Not in the middle of a call.
    useEffect(() => SipPhoneManager.instance.subscribe(() => onCallSet(!!SipPhoneManager.instance.getActiveCall())), [])

    const show = !!offer && !onCall

    const dismiss = () => {
        if (!offer) return
        getHub().Post(`/accounts/growth/offer/${offer.id}/dismiss`, {}, () => { }, () => { })
        if (offer.style === "banner") offerSet(undefined)
    }

    // The window is closed by its own script first and only unmounted in onClose -- unmounting
    // it while open leaves the backdrop over the page.
    const upgrade = () => {
        if (!offer) return
        getHub().Post(`/accounts/growth/offer/${offer.id}/click`, {}, () => { }, () => { })
        const code = offer.planCode
        if (offer.style === "banner") offerSet(undefined)
        navigate("/system/finance/subscription" + (code ? "?upgrade=" + code : ""))
    }

    if (!show) return null

    const Breakdown = () => (
        <div className="growth-offer-math">
            <div>
                <div className="growth-offer-math-title">{offer!.currentPlanName} — now</div>
                {offer!.currentLines.map((l, i) => <div key={i} className="growth-offer-math-line"><span>{l.label}</span><b>{money(l.amount)}</b></div>)}
                <div className="growth-offer-math-total"><span>About</span><b>{money(offer!.currentMonthly)}/mo</b></div>
            </div>
            <div className="is-plan">
                <div className="growth-offer-math-title">{offer!.planName}</div>
                {offer!.planLines.map((l, i) => <div key={i} className="growth-offer-math-line"><span>{l.label}</span><b>{money(l.amount)}</b></div>)}
                <div className="growth-offer-math-total"><span>About</span><b>{money(offer!.planMonthly)}/mo</b></div>
            </div>
        </div>
    )

    // At 80% of an allowance: a small card floating in a corner of the page -- there to be
    // noticed, never in the way of the work.
    if (offer!.style === "banner") {
        return (
            <div className="growth-offer-float" role="dialog" aria-label={offer!.headline}>
                <div className="growth-offer-float-head">
                    <span className="growth-offer-float-icon">
                        <Icon icon="arrow-up-right-dots" type="solid" size={14} />
                    </span>
                    <b>{offer!.headline}</b>
                    <button type="button" className="growth-offer-float-close" title="Not now" onClick={dismiss}>
                        <Icon icon="xmark" size={13} />
                    </button>
                </div>
                <div className="growth-offer-float-text">{offer!.text}</div>
                {offer!.alternative && <div className="growth-offer-float-alt">{offer!.alternative}</div>}
                <a href="#math" className="growth-offer-float-toggle" onClick={e => { e.preventDefault(); mathSet(m => !m) }}>
                    <Icon icon={math ? "chevron-up" : "chevron-down"} size={10} className="me-1" />
                    {math ? "Hide the math" : "See the math"}
                </a>
                {math && <Breakdown />}
                <div className="growth-offer-float-actions">
                    <button type="button" className="btn btn-sm btn-light" onClick={dismiss}>Not now</button>
                    <button type="button" className="btn btn-sm btn-primary" onClick={upgrade}>Move to {offer!.planName}</button>
                </div>
            </div>
        )
    }

    return (
        <Modal id={MODAL_ID} visible open
            title={offer!.headline ?? "A plan that fits"}
            titleIcon="arrow-up-right-dots"
            buttonText={"Move to " + offer!.planName}
            // Answered, not brushed aside: no close button, no closing by clicking behind it, no
            // Escape. "Not now" or the upgrade are the only ways out.
            staticBackdrop
            preventDiscard
            secondActionText="Not now"
            secondActionColor="light"
            onSecondAction={() => { dismiss(); window.closeModal(MODAL_ID) }}
            onConfirm={callback => { callback(false); upgrade() }}
            onClose={() => offerSet(undefined)}>
            <div className="growth-offer-modal">
                <p className="fs-6 text-gray-800">{offer!.text}</p>
                {offer!.alternative && <p className="text-gray-600 fs-7">{offer!.alternative}</p>}
                <Breakdown />
                <div className="text-gray-500 fs-8 mt-3">
                    Worked out from how your account has been used this month. You can compare every plan on your subscription page.
                </div>
            </div>
        </Modal>
    )
}
